import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import './AdminDashboard.css';
import { collection, getDoc, getDocs, doc, updateDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { PlusIcon, PackageIcon } from "../components/SvgIcons";

import {
  fetchShopItems,
  addShopItem,
  deleteShopItem,
  useShopItemsListener,
} from "../logic/AdminShopLogic";
import { toast } from 'react-toastify';
import { DEFAULT_APP_VERSION, getCurrentAppVersion, getRequiredVersion, isVersionOutdated, testVersionComparison } from '../utils/versionUtils';
import { setupVersionConfig, updateRequiredVersion, updateCurrentVersion } from '../utils/setupVersionConfig';
import { getLoggedInUser } from '../logic/loginLogic';
import { isUserAdmin } from '../utils/authUtils';

const AdminDashboard = () => {
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [rooms, setRooms] = useState([]);

  const [notifications, setNotifications] = useState([]);
  const [shopItems, setShopItems] = useState([]);
  const [newItem, setNewItem] = useState({ docId: "", name: "", description: "", priceDP: 0, stock: 0, imageUrl: "" });
  const [selectedCollection, setSelectedCollection] = useState("shopItems-v2");
  const [dpOptions, setDpOptions] = useState([]);
  const [newDPValue, setNewDPValue] = useState('');
  const [savingDPOptions, setSavingDPOptions] = useState(false);
  const [autoAssigningRanks, setAutoAssigningRanks] = useState(false);
  const [autoAssigningDivisions, setAutoAssigningDivisions] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAllNotifications, setSelectAllNotifications] = useState(false);
  const [dpInstructions, setDpInstructions] = useState([]);
  const [versionInfo, setVersionInfo] = useState({
    currentVersion: DEFAULT_APP_VERSION,
    requiredVersion: '',
    isOutdated: false
  });
  const [newRequiredVersion, setNewRequiredVersion] = useState('');
  const [newCurrentVersion, setNewCurrentVersion] = useState(DEFAULT_APP_VERSION);
  const [updatingVersion, setUpdatingVersion] = useState(false);



  const handleToggleNotification = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((nid) => nid !== id) : [...prev, id]
    );
  };
  const handleToggleSelectAllNotifications = () => {
    if (selectAllNotifications) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map((n) => n.id));
    }
    setSelectAllNotifications(!selectAllNotifications);
  };
  const handleDeleteSelectedNotifications = async () => {
    if (!window.confirm('Delete selected notifications?')) return;

    try {
      for (const id of selectedNotifications) {
        await deleteDoc(doc(db, "adminNotifications", id));
      }
      toast.success('✅ Selected notifications deleted!');
      setSelectedNotifications([]);
      setSelectAllNotifications(false);

      // Refresh list
      const notifSnap = await getDocs(collection(db, "adminNotifications"));
      const notifList = notifSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const aTime = a.timestamp?.toDate?.()?.getTime?.() || 0;
          const bTime = b.timestamp?.toDate?.()?.getTime?.() || 0;
          return bTime - aTime;
        });
      setNotifications(notifList);
    } catch (error) {
      toast.error('❌ Error deleting notifications.');
    }
  };


  const fetchRooms = async () => {
    const querySnapshot = await getDocs(collection(db, 'duelRooms'));
    const roomList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setRooms(roomList);
  };
  const handleOpenRoomModal = async () => {
    await fetchRooms();
    setShowRoomModal(true);
  };
  const handleDeleteRoom = async (roomId) => {
    await deleteDoc(doc(db, 'duelRooms', roomId));
    // Refresh room list after deletion
    fetchRooms();
  };

  const handleSaveDPOptions = async () => {
    setSavingDPOptions(true);
    try {
      await setDoc(doc(db, 'settings', 'dpOptions'), {
        options: dpOptions,
        instructionsList: dpInstructions,
      });
      toast.success('✅ DP Options saved!');
    } catch (error) {
      toast.error('❌ Failed to save DP options.');
    } finally {
      setSavingDPOptions(false);
    }
  };

  const handleRemoveDPOption = (index) => {
    const updated = [...dpOptions];
    updated.splice(index, 1);
    setDpOptions(updated);
  };










  useEffect(() => {
    const fetchDPOptions = async () => {
      try {
        const docRef = doc(db, 'settings', 'dpOptions');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setDpOptions(docSnap.data().options || []);
          setDpInstructions(docSnap.data().instructionsList || []);
        } else {
          // Removed: console.log('No dpOptions found yet');
        }
      } catch (error) {
        // Removed: console.error('Error fetching dpOptions:', error);
      }
    };

    const fetchVersionInfo = async () => {
      try {
        // Current version is now local (from package.json), not from Firestore
        const current = DEFAULT_APP_VERSION;
        const required = await getRequiredVersion();
        const outdated = isVersionOutdated(current, required);
        setVersionInfo({
          currentVersion: current,
          requiredVersion: required,
          isOutdated: outdated
        });
        setNewRequiredVersion(required);
        setNewCurrentVersion(current);
      } catch (error) {
        console.error('Error fetching version info:', error);
      }
    };

    fetchDPOptions();
    fetchVersionInfo();
  }, []);









  // Real-time shop items listener
  useShopItemsListener(selectedCollection, setShopItems);

  // Load notifications (one-time fetch)
  useEffect(() => {
    const loadNotifications = async () => {
      const notifSnap = await getDocs(collection(db, "adminNotifications"));
      const notifList = notifSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const aTime = a.timestamp?.toDate?.()?.getTime?.() || 0;
          const bTime = b.timestamp?.toDate?.()?.getTime?.() || 0;
          return bTime - aTime; // newest first
        });
      setNotifications(notifList);
    };
    loadNotifications();
  }, []);
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;


    if (user) {
      user.getIdTokenResult(true).then((idTokenResult) => {
        if (!idTokenResult.claims.admin) {
          toast.info('⚠️ You are not recognized as an admin. Please log out and log back in.');
        }
      }).catch((err) => {
        // Removed: console.error("Error fetching ID token:", err);
      });
    }
  }, []);

  const handleAddDPOption = () => {
    if (!newDPValue.trim()) {
      toast.info('Please enter a valid option.');
      return;
    }
    setDpOptions([...dpOptions, newDPValue.trim()]);

    setNewDPValue('');
  };


  const handleAutoAssignRanks = async () => {
    setAutoAssigningRanks(true);
    try {
      // 1. Fetch all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // 2. Sort descending by YP
      const sorted = userList.sort((a, b) => (b.YP || 0) - (a.YP || 0));

      // 3. Assign ranks
      for (let i = 0; i < sorted.length; i++) {
        const userId = sorted[i].id;
        const newRank = i + 1;
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { rank: newRank });
      }

      toast.success('✅ All user ranks updated automatically based on YP!');
    } catch (err) {
      toast.error('❌ Failed to auto-assign ranks. See console for details.');
    } finally {
      setAutoAssigningRanks(false);
    }
  };





  const sanitizeForDocId = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '-')   // replace invalid characters with dash
      .replace(/--+/g, '-')           // merge consecutive dashes
      .replace(/^-+|-+$/g, '');       // remove leading/trailing dashes
  };
  const handleAddItem = async () => {
    setAddingItem(true);
    const { docId, name, description, priceDP, stock, imageUrl } = newItem;
    if (!docId || !name || !description || !imageUrl || !priceDP || stock < 0) {
      toast.info('All fields are required.');
      return;
    }
    await addShopItem({ name, description, priceDP, stock, imageUrl }, docId, selectedCollection);

    // Real-time listener will automatically update the items
    setNewItem({ docId: "", name: "", description: "", priceDP: 0, stock: 0, imageUrl: "" });
    toast.success('✅ Item added!');
    setAddingItem(false);
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await deleteShopItem(id);
    // Real-time listener will automatically update the items
  };

  const navigate = useNavigate();



  const handleAutoAssignDivisions = async () => {
    setAutoAssigningDivisions(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by YP (descending), then by matches (descending)
      const sortedUsers = userList.sort((a, b) => {
        const aYP = a.YP || 0;
        const bYP = b.YP || 0;
        
        if (aYP !== bYP) {
          return bYP - aYP; // Higher YP first
        }
        
        // If YP is the same, sort by matches
        const aMatches = a.stats?.matches || 0;
        const bMatches = b.stats?.matches || 0;
        return bMatches - aMatches; // Higher matches first
      });

      // Update both ranks and divisions based on sorted position
      for (let i = 0; i < sortedUsers.length; i++) {
        const user = sortedUsers[i];
        const rank = i + 1;
        let division = 'Red Slifer';
        
        if (rank >= 1 && rank <= 4) {
          division = 'Blue Obelisk';
        } else if (rank >= 5 && rank <= 12) {
          division = 'Yellow Ra';
        }

        const userRef = doc(db, 'users', user.id);
        await updateDoc(userRef, { 
          rank: rank,
          division: division
        });
      }

      toast.success('✅ All user ranks and divisions updated automatically based on YP and matches!');
    } catch (err) {
      toast.error('❌ Failed to auto-assign ranks and divisions. See console for details.');
    } finally {
      setAutoAssigningDivisions(false);
    }
  };

  // Version Control Functions
  const handleUpdateRequiredVersion = async () => {
    if (!newRequiredVersion.trim()) {
      toast.error('Please enter a valid version number.');
      return;
    }

    setUpdatingVersion(true);
    try {
      await updateRequiredVersion(newRequiredVersion.trim());
      
      // Refresh version info
      const current = await getCurrentAppVersion();
      const required = await getRequiredVersion();
      const outdated = isVersionOutdated(current, required);
      setVersionInfo({
        currentVersion: current,
        requiredVersion: required,
        isOutdated: outdated
      });
      
      toast.success(`✅ Required version updated to ${newRequiredVersion.trim()}`);
    } catch (error) {
      toast.error('❌ Failed to update required version.');
      console.error('Error updating version:', error);
    } finally {
      setUpdatingVersion(false);
    }
  };

  const handleResetToCurrentVersion = async () => {
    setUpdatingVersion(true);
    try {
      // Current version is now local (from package.json), not from Firestore
      const current = DEFAULT_APP_VERSION;
      await updateRequiredVersion(current);
      
      setVersionInfo({
        currentVersion: current,
        requiredVersion: current,
        isOutdated: false
      });
      setNewRequiredVersion(current);
      
      toast.success(`✅ Required version reset to current version (${current})`);
    } catch (error) {
      toast.error('❌ Failed to reset version.');
      console.error('Error resetting version:', error);
    } finally {
      setUpdatingVersion(false);
    }
  };

  const handleTestVersionGate = async () => {
    const testVersion = '999.999.999'; // Very high version to force blocking
    setUpdatingVersion(true);
    try {
      await updateRequiredVersion(testVersion);
      toast.success(`✅ Version gate test activated! Required version set to ${testVersion}. Regular users will now be blocked.`);
      
      // Refresh version info
      const current = DEFAULT_APP_VERSION; // Current version is now local
      const required = await getRequiredVersion();
      const outdated = isVersionOutdated(current, required);
      setVersionInfo({
        currentVersion: current,
        requiredVersion: required,
        isOutdated: outdated
      });
      setNewRequiredVersion(testVersion);
      
      // Test version comparison
      testVersionComparison();
      console.log('Current version check:', {
        current: current,
        required: required,
        outdated: outdated
      });
    } catch (error) {
      toast.error('❌ Failed to test version gate.');
      console.error('Error testing version gate:', error);
    } finally {
      setUpdatingVersion(false);
    }
  };

  const handleTestCurrentVersionStatus = async () => {
    try {
      const current = DEFAULT_APP_VERSION; // Current version is now local
      const required = await getRequiredVersion();
      const outdated = isVersionOutdated(current, required);
      console.log('Current version status:', {
        current: current,
        required: required,
        outdated: outdated
      });
      toast.info(`Current: ${current}, Required: ${required}, Outdated: ${outdated}`);
    } catch (error) {
      toast.error('❌ Error checking version status.');
    }
  };

  const handleTestAdminDetection = async () => {
    try {
      const user = getLoggedInUser();
      if (user) {
        const adminStatus = await isUserAdmin(user.uid);
        console.log('Admin detection test:', {
          userId: user.uid,
          isAdmin: adminStatus
        });
        toast.info(`User: ${user.uid}, Admin: ${adminStatus}`);
      } else {
        toast.info('No user logged in');
      }
    } catch (error) {
      toast.error('❌ Error testing admin detection.');
    }
  };

  const handleSetupVersionConfig = async () => {
    try {
      await setupVersionConfig(DEFAULT_APP_VERSION);
      toast.success('✅ Version config set up successfully!');
      
      // Refresh version info
      const current = await getCurrentAppVersion();
      const required = await getRequiredVersion();
      const outdated = isVersionOutdated(current, required);
      setVersionInfo({
        currentVersion: current,
        requiredVersion: required,
        isOutdated: outdated
      });
      setNewRequiredVersion(required);
      setNewCurrentVersion(current);
    } catch (error) {
      toast.error('❌ Error setting up version config.');
      console.error('Error setting up version config:', error);
    }
  };

  const handleUpdateCurrentVersion = async () => {
    if (!newCurrentVersion.trim()) {
      toast.error('Please enter a valid version number.');
      return;
    }

    setUpdatingVersion(true);
    try {
      // Update the current version in Firestore
      await updateCurrentVersion(newCurrentVersion.trim());
      
      // Refresh version info
      const current = await getCurrentAppVersion();
      const required = await getRequiredVersion();
      const outdated = isVersionOutdated(current, required);
      setVersionInfo({
        currentVersion: current,
        requiredVersion: required,
        isOutdated: outdated
      });
      
      // Update the input field
      setNewCurrentVersion(current);
      
      toast.success(`✅ Current version updated to ${current}`);
      
      console.log('Version updated:', {
        newCurrent: current,
        required: required,
        outdated: outdated
      });
    } catch (error) {
      toast.error('❌ Failed to update current version.');
      console.error('Error updating current version:', error);
    } finally {
      setUpdatingVersion(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-section">
        <h2>⚡ Rank Management</h2>
        <button onClick={handleAutoAssignRanks} disabled={autoAssigningRanks}>
          Auto-Assign Ranks by YP
        </button>
        <button onClick={handleAutoAssignDivisions} disabled={autoAssigningDivisions}>
          Assign Ranks & Divisions by YP
        </button>
        <button onClick={() => navigate('/admin/leaderboard-archive')}>
          📊 Leaderboard Archive Manager
        </button>
        <button onClick={() => navigate('/admin/rewards')}>
          🏆 Reward System
        </button>
        <button onClick={handleOpenRoomModal}>CURRENT ROOM</button>

      </div>



      <div className="admin-section">
        <h2>🎮 Tournament Management</h2>
        <button onClick={() => navigate('/admin/tournament')}>
          Open Tournament Manager
        </button>
        <button onClick={() => navigate('/admin/tournament-participation')}>
          Tournament Participation Tracker
        </button>
        <button onClick={() => navigate('/admin/tournament-notification-test')}>
          Test Tournament Notifications
        </button>
      </div>

      <div className="admin-section">
        <h2>🗺️ Quest Management</h2>
        <button onClick={() => navigate('/admin/quests')}>
          Open Quest Manager
        </button>
      </div>

      <div className="admin-section">
        <h2>🗺️ User Quest Progress Management</h2>
        <button onClick={() => navigate('/admin/user-quests')}>
          Open User Quest Manager
        </button>
      </div>


      <div className="admin-section">
        <h2>🏅 User Achievement Management</h2>
        <button onClick={() => navigate('/admin/achievements')}>
          Open Achievement Manager
        </button>
      </div>

      <div className="admin-section">
        <h2>👥 User Management</h2>
        <button onClick={() => navigate('/admin/manage-users')}>
          Open User Manager
        </button>
      </div>

      <div className="admin-section">
        <h2>🔒 Security & Logs</h2>
        <button onClick={() => navigate('/admin/logs')}>
          View Security Logs
        </button>
        <button onClick={() => navigate('/admin-panelx-952504/security-dashboard')}>
          Security Compliance Dashboard
        </button>
      </div>


      <div className="admin-section">
        <h2>🛒 Shop Management</h2>
        <button onClick={() => navigate('/admin/shop-manager')}>
          Open Shop Item Manager
        </button>
      </div>

      <div className="admin-section">
        <h2><PackageIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Purchase Management</h2>
        <button onClick={() => navigate('/admin/manage-purchases')}>
          Open Purchase Manager
        </button>
      </div>

      <div className="admin-section">
        <h2>🔒 Version Control System</h2>
        <div style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7',
          padding: '10px', 
          borderRadius: '6px', 
          marginBottom: '15px',
          fontSize: '14px'
        }}>
          <strong>📱 New Version System:</strong> Current app version is now local (from package.json) and cannot be changed from admin panel. 
          Users must download new APKs to get version updates. Required version can still be set from here to control access.
        </div>
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '15px', 
          borderRadius: '8px', 
          marginBottom: '15px' 
        }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Current App Version:</strong> {versionInfo.currentVersion}
          </div>
          <div style={{ marginBottom: '10px' }}>
            <strong>Required Version:</strong> {versionInfo.requiredVersion}
          </div>
          <div style={{ 
            marginBottom: '10px',
            color: versionInfo.isOutdated ? '#ff6b6b' : '#4CAF50',
            fontWeight: 'bold'
          }}>
            Status: {versionInfo.isOutdated ? '⚠️ OUTDATED' : '✅ UP TO DATE'}
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Current App Version:</strong>
            <span style={{ 
              marginLeft: '10px',
              padding: '8px 12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              border: '1px solid #2196F3',
              color: '#1976d2',
              fontWeight: 'bold'
            }}>
              {versionInfo.currentVersion} (Local - Update via APK)
            </span>
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '5px',
              fontStyle: 'italic'
            }}>
              ℹ️ Current version is now local and cannot be changed from admin panel. 
              Users must download a new APK to get version updates.
            </div>
          </div>
          <div>
            <strong>Required Version:</strong>
            <input
              type="text"
              value={newRequiredVersion}
              onChange={(e) => setNewRequiredVersion(e.target.value)}
              placeholder="Enter new required version (e.g., 1.1.0)"
              style={{ 
                padding: '8px', 
                marginLeft: '10px',
                marginRight: '10px', 
                width: '200px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
            <button 
              onClick={handleUpdateRequiredVersion} 
              disabled={updatingVersion}
              style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {updatingVersion ? 'Updating...' : 'Update Required Version'}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <button 
            onClick={handleResetToCurrentVersion}
            disabled={updatingVersion}
            style={{
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '5px'
            }}
          >
            Reset to Current Version
          </button>
          <button 
            onClick={handleTestVersionGate}
            disabled={updatingVersion}
            style={{
              backgroundColor: '#ff9800',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '5px'
            }}
          >
            Test Version Gate (Block All Users)
          </button>
          <button 
            onClick={handleTestCurrentVersionStatus}
            style={{
              backgroundColor: '#9c27b0',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '5px'
            }}
          >
            Test Current Status
          </button>
          <button 
            onClick={handleTestAdminDetection}
            style={{
              backgroundColor: '#607d8b',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '5px'
            }}
          >
            Test Admin Detection
          </button>
          <button 
            onClick={handleSetupVersionConfig}
            style={{
              backgroundColor: '#795548',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Setup Version Config
          </button>
        </div>

        <div style={{ 
          fontSize: '12px', 
          color: '#666',
          backgroundColor: '#fff3cd',
          padding: '10px',
          borderRadius: '4px',
          border: '1px solid #ffeaa7'
        }}>
          <strong>💡 Version Control Tips:</strong><br/>
          • Set required version higher than current to block outdated apps<br/>
          • Use "Test Version Gate" to simulate blocking all users<br/>
          • Use "Reset to Current Version" to allow all users<br/>
          • Admin users can always access admin pages regardless of version
        </div>
      </div>


      <div className="admin-section">
        <h2>💰 Manage DP Options</h2>
        <div style={{ marginBottom: '10px' }}>
          <input
            type="text"
            value={newDPValue}
            onChange={(e) => setNewDPValue(e.target.value)}
            placeholder="e.g. 15 DP = 1,500 MGA"
          />

          <button onClick={handleAddDPOption}>Add Option</button>
        </div>

        <ul style={{ listStyle: 'none', padding: 0 }}>
          {dpOptions.map((dp, index) => (
            <li key={index} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                value={dp}
                onChange={(e) => {
                  const updated = [...dpOptions];
                  updated[index] = e.target.value;
                  setDpOptions(updated);
                }}
                style={{ flex: 1, marginRight: '10px' }}
              />
              <button onClick={() => handleRemoveDPOption(index)}>Remove</button>
            </li>
          ))}
        </ul>
        <h4>📋 Instructions (shown in the DP Modal)</h4>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {dpInstructions.map((line, index) => (
            <li key={index} style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
              <textarea
                value={line}
                onChange={(e) => {
                  const updated = [...dpInstructions];
                  updated[index] = e.target.value;
                  setDpInstructions(updated);
                }}
                style={{ flex: 1, marginRight: '10px' }}
                rows={2}
              />
              <button onClick={() => {
                const updated = [...dpInstructions];
                updated.splice(index, 1);
                setDpInstructions(updated);
              }}>
                Remove
              </button>
            </li>
          ))}
        </ul>

        <div style={{ marginBottom: '10px' }}>
          <button onClick={() => setDpInstructions([...dpInstructions, ""])}><PlusIcon size={16} className="mr-2" />Add Instruction</button>
        </div>



        <button onClick={handleSaveDPOptions} disabled={savingDPOptions}>Save DP Options</button>
      </div>

      <h2>🔔 Notifications</h2>
      <button onClick={handleDeleteSelectedNotifications} disabled={selectedNotifications.length === 0}>
        Delete Selected
      </button>
      <table>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectAllNotifications}
                onChange={handleToggleSelectAllNotifications}
              />
            </th>
            <th>Text</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {notifications.map((n) => {
            const date =
              n.timestamp && typeof n.timestamp.toDate === "function"
                ? new Date(n.timestamp.toDate()).toLocaleString()
                : "Unknown time";
            return (
              <tr key={n.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedNotifications.includes(n.id)}
                    onChange={() => handleToggleNotification(n.id)}
                  />
                </td>
                <td>
                  {n.pseudo ? <strong>{n.pseudo}</strong> : <em>Unknown</em>} – {n.text}
                </td>
                <td>{date}</td>
              </tr>
            );
          })}
        </tbody>
      </table>


      {showRoomModal && (
        <div className="modal-overlay" onClick={() => setShowRoomModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Current Rooms</h2>
            {rooms.length === 0 ? (
              <p>No rooms found.</p>
            ) : (
              <ul className="room-list">
                {rooms.map(room => (
                  <li key={room.id} className="room-item">
                    <div><strong>ID:</strong> {room.id}</div>
                    <div><strong>Status:</strong> {room.status}</div>
                    <button onClick={() => handleDeleteRoom(room.id)}>Delete</button>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => setShowRoomModal(false)}>Close</button>
          </div>
        </div>
      )}


    </div>
  );
};

export default AdminDashboard;
